import React, { useCallback } from "react";
import { View, ScrollView, Text } from "react-native";
import { AppBar } from "@/elements/components/ui/app-bar";
import { useRouter } from "expo-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import ChildCard from "@/elements/components/shared/child-card";
import { Button } from "@/elements/components/ui/button";
import PlusIcon from "@/assets/svgs/plus-icon";
import { useAuthActions } from "@convex-dev/auth/react";
import ResponsiveContent from "@/elements/components/shared/responsive-content";

function ChildDetailsScreen() {
  const { signOut } = useAuthActions();
  const router = useRouter();
  const { data: children = [] } = useQuery(
    convexQuery(api.parent.onboarding.queries.getAllChildrenByParentId, {})
  );
  const { data: curriculums = [] } = useQuery(
    convexQuery(api.tutor.onboarding.queries.getAvailableCurriculums, {})
  );
  const { data: yearGroups = [] } = useQuery(
    convexQuery(api.parent.onboarding.queries.getYearGroupsList, {})
  );
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.update),
  });

  const handleAddChild = useCallback(() => {
    router.push("/(app)/onboarding/(parent)/add-child");
  }, [router]);

  const handleCreateAccount = useCallback(async () => {
    await updateProfile({
      onboardingStepCompleted: "addChildren",
    });
    router.replace("/(app)/onboarding/(parent)/parent-legal");
  }, [router, updateProfile]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon
          onPress={() => (router.canGoBack() ? router.back() : signOut())}
        />
        <AppBar.Title>{router.canGoBack() ? "Back" : "Sign out"}</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-8" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Confirm your child&apos;s profile
              </Text>
              <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                Please make sure the details are correct.
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-8">
              {children.map((child, index) => (
                <View key={child._id} className="gap-6">
                  <Text className="font-geologica-medium font-medium text-base text-black leading-[1.5]">
                    Child {index + 1}
                  </Text>
                  <ChildCard
                    child={child}
                    curriculums={curriculums}
                    yearGroups={yearGroups}
                  />
                </View>
              ))}
              <View className="gap-4">
                <Button
                  fullWidth
                  intent="primary"
                  outline
                  icon
                  onPress={handleAddChild}
                  size="md"
                >
                  <Button.Icon
                    as={PlusIcon}
                    intent="primary"
                    className="size-5"
                  />
                  <Button.Text size="md" intent="primary">
                    {children.length === 0 ? `Add child` : `Add another child`}
                  </Button.Text>
                </Button>

                <Button
                  onPress={handleCreateAccount}
                  disabled={children.length === 0}
                  fullWidth
                  intent="primary"
                  size="md"
                >
                  <Button.Text size="md">Get Started!</Button.Text>
                </Button>
              </View>
            </View>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default ChildDetailsScreen;
